import type { Immutable } from "immer";
import React from "react";
import { jt } from "ttag";

import type { MediaObjectBlock as MediaObjectBlockValue } from "@reactivated";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/flagship/media_object.html`` (FlagshipPage ``MediaObjectBlock``).
 *
 * The ``.callouts`` wrapper + the hard-coded "Why visit a Tempur-Pedic® store?"
 * lead-in (a ``{% blocktrans %}`` with an inline ``<sup>®</sup>``, reproduced
 * via ``jt``) move into React. Each column's ``copy`` is a ``RichTextBlock``,
 * which Reactivated serializes to EXPANDED HTML already wrapped in
 * ``<div class="rich-text">`` (``wagtail.contrib.legacy.richtext`` is
 * installed), so it is injected raw via ``dangerouslySetInnerHTML`` -- matching
 * the legacy ``{{ column.copy|safe }}`` byte-for-byte (no extra wrapper added).
 * ``headline`` is plain ``CharBlock`` text.
 */
export const FlagshipMediaObjectBlock = (
    props: Immutable<{ value: MediaObjectBlockValue }>,
): React.JSX.Element => {
    const columns = props.value.columns ?? [];
    // jt preserves the inline <sup> of the legacy ``{% blocktrans %}`` headline.
    const reg = <sup key="reg">&reg;</sup>;
    return (
        <section className="callouts l-capped-width">
            <div className="lead-in">
                <h1 className="lead-in__headline">
                    {jt`Why visit a Tempur-Pedic${reg} store?`}
                </h1>
            </div>
            {columns.map((column, i) => (
                <div className="callouts__item" key={i}>
                    <h3 className="callouts__headline">{column.headline}</h3>
                    <div
                        className="callouts__descript"
                        dangerouslySetInnerHTML={{ __html: column.copy ?? "" }}
                    />
                </div>
            ))}
        </section>
    );
};
